import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { POPUP_OVERLAY_CONFIG } from "../../../pixiSettings";

import { SOUND_NAME } from "../../../sounds";
import anime from "animejs/lib/anime.es.js";
import PotCollectionReloadController from "./PotCollectionReloadController";
import { attemptFullscreenToggle, safeInit } from "../../../utilities";
import { selectShowPotCollectionReload } from "../../../State/Slices/animationsSlice";
import store from "../../../State/Store/store";
import { soundManager } from "../../../SoundManager";

/**
 * PotCollectionReloadDialog
 *
 * Reload/resume popup for the Pot Collection bonus game.
 * Shown when the user reloads mid-bonus (after at least one respin).
 *
 * Displays:
 *   - "ZEUS RESPINS" logo (embedded in spine)
 *   - Respins remaining number (GrekoNew counter font via txt_slot_value)
 *   - "RESPINS LEFT" label
 *   - "TAP TO CONTINUE" button
 *
 * Identical structure to PotCollectionGrandJackpotDialog.
 */
const PotCollectionReloadDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showReloadDialog = useSelector(selectShowPotCollectionReload);

  const reloadControllerRef = useRef(null);
  const reloadContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const handleButtonClickRef = useRef(null);
  const dimensionsContext = useDimensions();
  const {
    dimensions,
    uiSymbolHeight: uiSymbolSize,
    device,
  } = dimensionsContext;

  // Overlay state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  // Expose fadeInBackground / fadeOutBackground via overlayRef
  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: POPUP_OVERLAY_CONFIG.ALPHA,
        duration: POPUP_OVERLAY_CONFIG.FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: POPUP_OVERLAY_CONFIG.ALPHA },
        alpha: 0,
        duration: POPUP_OVERLAY_CONFIG.FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // ── Click anywhere: fullscreen + dismiss popup ───────────────────────
  const handleButtonClick = useCallback(
    async (event) => {
      if (event?.stopPropagation) event.stopPropagation();
      
      // Trigger fullscreen first
      await attemptFullscreenToggle(dispatch, device);
      
      // Then handle popup dismissal via controller
      if (reloadControllerRef.current) {
        const accepted = reloadControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [dispatch, device],
  );
  handleButtonClickRef.current = handleButtonClick;

  // Initialize controller once
  useEffect(() => {
    if (reloadContainerRef.current && !reloadControllerRef.current) {
      safeInit(() => {
        reloadControllerRef.current = new PotCollectionReloadController(
          reloadContainerRef.current,
          dimensionsContext,
          dispatch,
          uiSymbolSize,
          overlayRef,
          t,
        );
        // Wire button click handler via ref proxy — always calls latest version
        reloadControllerRef.current.setButtonClickHandler((...args) => {
          if (handleButtonClickRef.current) handleButtonClickRef.current(...args);
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (reloadControllerRef.current) {
      reloadControllerRef.current.updateDimensions(
        dimensionsContext,
        uiSymbolSize,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // Trigger the sequence when showReloadDialog becomes true
  useEffect(() => {
    if (reloadControllerRef.current && showReloadDialog) {
      // Get respins left from potCollection state
      const pcState = store.getState().potCollection;
      const respinsLeft = pcState?.respinsLeft;
      reloadControllerRef.current.setRespinsLeft(respinsLeft);
      reloadControllerRef.current.startSequence();
    }
  }, [showReloadDialog]);

  // Add spacebar handling
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      if (!showReloadDialog || !reloadControllerRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        const accepted = reloadControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      } catch {
        if (
          reloadControllerRef.current &&
          reloadControllerRef.current.finishAnimation
        ) {
          reloadControllerRef.current.finishAnimation();
        }
      }
    };

    if (showReloadDialog) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false,
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [showReloadDialog, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reloadControllerRef.current) {
        reloadControllerRef.current.cleanup();
      }
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  // Memoize container position
  const containerPosition = useMemo(
    () => ({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    }),
    [dimensions.width, dimensions.height],
  );

  // Overlay draw function
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(POPUP_OVERLAY_CONFIG.COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="pot_collection_reload_dialog"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={10}
      eventMode={showReloadDialog ? "auto" : "none"}
      ref={reloadContainerRef}
    >
      {/* Background overlay */}
      <Graphics
        name="pot_collection_reload_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={showReloadDialog ? "static" : "none"}
        cursor="default"
        pointertap={handleButtonClick}
      />

      {/* Spine animation, text, and button managed by PotCollectionReloadController */}
    </Container>
  );
};

export default memo(PotCollectionReloadDialog);
